import React, { useEffect, useState } from 'react'

import { SettingOutlined } from '@ant-design/icons'
import { Checkbox as AntDCheckbox, Collapse, Input, Popover, Select, Table as AntDTable, Typography } from 'antd'
import idx from 'idx'
import PropTypes from 'prop-types'

import Checkbox from '../Checkbox/Checkbox'
import Label from '../Label/Label'
import Spinner from '../Spinner/Spinner'
import styles from './Table.module.css'
const { Text } = Typography
const { Search } = Input

const DEFAULT_PAGINATION = {
  current: 1,
  pageSize: 10,
  pageSizeOptions: [10, 20, 50]
}

const Table = ({
  bordered,
  columns,
  defaultVisibleColumns,
  fetchData,
  filterKey,
  filterOptions,
  filterValue,
  params,
  paginationLegend,
  results,
  rowClassName,
  rowKey,
  secondaryFilter,
  showSecondaryFilter,
  totalCount
}) => {
  const allColumnKeys = columns.map(c => c.dataIndex || c.title)
  const initialVisible = defaultVisibleColumns && defaultVisibleColumns.length > 0
    ? defaultVisibleColumns
    : allColumnKeys
  const [visibleColumns, setVisibleColumns] = useState(initialVisible)
  const [data, setData] = useState([])
  const [filteredInfo, setFilteredInfo] = useState(null)
  const [filteredItem, setFilteredItem] = useState('')
  const [secondaryFilteredItem, setSecondaryFilteredItem] = useState([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION)
  const [searchItem, setSearchItem] = useState(null)
  const [sortedInfo, setSortedInfo] = useState(null)
  const [footerText, setFooterText] = useState('')
  const [isSecondaryFilterSelected, setIsSecondaryFilterSelected] = useState(false)

  const handleColumnToggle = (columnKey) => {
    setVisibleColumns(prev =>
      prev.includes(columnKey)
        ? prev.filter(k => k !== columnKey)
        : [...prev, columnKey]
    )
  }

  const handleFilterChange = value => {
    setFilteredItem(value)
    setLoading(true)
    fetchData({
      [filterKey]: value,
      pagination: DEFAULT_PAGINATION,
      ...(searchItem && { searchBy: searchItem }),
      ...(showSecondaryFilter && { showExpired: isSecondaryFilterSelected }),
      ...(secondaryFilter && { venueId: secondaryFilteredItem })
    })
  }

  const handleSecondaryFilterChange = value => {
    setSecondaryFilteredItem(value)
    setLoading(true)
    fetchData({
      [filterKey]: filteredItem,
      pagination: DEFAULT_PAGINATION,
      ...(searchItem && { searchBy: searchItem }),
      ...(showSecondaryFilter && { showExpired: isSecondaryFilterSelected }),
      ...(secondaryFilter && { venueId: value })
    })
  }

  useEffect(() => {
    if (filterValue) {
      setFilteredItem(filterValue)
    }
  }, [filterValue, filterKey])

  useEffect(() => {
    if (results) {
      setLoading(false)
      setData(results)
      setPagination({
        ...params.pagination,
        total: totalCount
      })
    }
  }, [fetchData, params, results, totalCount])

  useEffect(() => {
    if (params && params.pagination) {
      updatePaginationText(params.pagination, totalCount)
    }
  }, [params, totalCount])

  useEffect(() => {
    if (secondaryFilter && secondaryFilter.list) {
      const venuesList = secondaryFilter.defaultValue
        ? secondaryFilter.defaultValue
        : secondaryFilter.list.map(venue => venue.value)
      setSecondaryFilteredItem(venuesList)
      fetchData({
        [filterKey]: filteredItem,
        pagination: DEFAULT_PAGINATION,
        ...(searchItem && { searchBy: searchItem }),
        ...(showSecondaryFilter && { showExpired: isSecondaryFilterSelected }),
        ...(secondaryFilter && { venueId: venuesList })
      })
    }
  }, [fetchData, filterKey, filteredItem, isSecondaryFilterSelected, searchItem, secondaryFilter, showSecondaryFilter])

  useEffect(() => {
    setLoading(true)
    const options = filterValue
      ? { pagination, [filterKey]: filterValue }
      : { pagination }
    fetchData(options)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const updatePaginationText = (paginationParams, totalCount) => {
    const { current, pageSize } = paginationParams
    const footerText = `Showing ${(pageSize * (current - 1)) + 1} to ${(totalCount < pageSize) ? totalCount : (current * pageSize)} of ${totalCount} entries`
    setFooterText(footerText)
  }

  const handleSearch = value => {
    setSearchItem(value)
    setLoading(true)
    fetchData({
      ...(filteredItem && { [filterKey]: filteredItem }),
      pagination: DEFAULT_PAGINATION,
      searchBy: value,
      ...(showSecondaryFilter && { showExpired: isSecondaryFilterSelected }),
      ...(secondaryFilter && { venueId: secondaryFilteredItem })
    })
  }

  const handleTableChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters)
    setSortedInfo(sorter)
    setLoading(true)
    fetchData({
      ...filters,
      ...(filteredItem && { [filterKey]: filteredItem }),
      ...(searchItem && { searchBy: searchItem }),
      ...(showSecondaryFilter && { showExpired: isSecondaryFilterSelected }),
      ...(secondaryFilter && { venueId: secondaryFilteredItem }),
      pagination,
      sortField: sorter.field,
      sortOrder: sorter.order
    })
  }

  const columnsWithFilterSort = columns
    .filter(column => {
      const key = column.dataIndex || column.title
      return visibleColumns.includes(key)
    })
    .map(column => {
      const dataIndex = idx(column, _ => _.dataIndex)
      const hasFilter = Boolean(idx(column, _ => _.filters))
      const hasSort = Boolean(idx(column, _ => _.sorter))
      const filteredValue = idx(filteredInfo, _ => _[dataIndex]) || null
      const sortOrder = (
        idx(sortedInfo, _ => _.field) === dataIndex &&
        idx(sortedInfo, _ => _.order)
      )

      return {
        ...column,
        ...(hasFilter && { filteredValue }),
        ...(hasSort && { sortOrder })
      }
    })

  const columnPickerContent = (
    <div className={styles.columnPicker}>
      {columns.map(column => {
        const key = column.dataIndex || column.title
        return (
          <div key={key} className={styles.columnPickerItem}>
            <AntDCheckbox
              checked={visibleColumns.includes(key)}
              onChange={() => handleColumnToggle(key)}
            >
              {column.title}
            </AntDCheckbox>
          </div>
        )
      })}
    </div>
  )

  const onSecondaryFilterChange = event => {
    setIsSecondaryFilterSelected(event.target.checked)
    setLoading(true)
    fetchData({
      ...(filteredItem && { [filterKey]: filteredItem }),
      pagination: DEFAULT_PAGINATION,
      ...(searchItem && { searchBy: searchItem }),
      ...(showSecondaryFilter && { showExpired: event.target.checked })
    })
  }

  const hasFilter = filterKey && filterOptions
  const filterJsx = hasFilter && (
    <div className={styles.filterRow}>
      <Label className={styles.filterLabel} value="Filter" />
      <Select
        className={styles.filter}
        onChange={handleFilterChange}
        options={filterOptions}
        value={filteredItem}
      />
    </div>
  )

  const secondaryFilterDropdown = secondaryFilter && (
    <div className={styles.filterRow}>
      <Label className={styles.filterLabel} value={secondaryFilter.label} />
      <Select
        className={`${styles.filter} ${styles.filterWide}`}
        mode="multiple"
        onChange={handleSecondaryFilterChange}
        options={secondaryFilter.list}
        value={secondaryFilteredItem}
      />
    </div>
  )
  const showPaginationText = totalCount > 0
  const paginationText = showPaginationText && <Text className={styles.tableEntries}>{footerText}</Text>
  const paginationConfig = {
    ...pagination,
    ...(paginationLegend && {
      showTotal: () => paginationLegend
    })
  }
  const secondaryFilterComponent = secondaryFilter
    ? secondaryFilterDropdown
    : showSecondaryFilter && (
      <div className={`${styles.filterRow} ${styles.checkboxRow}`}>
        <Checkbox checked={isSecondaryFilterSelected} isInline label='Show Expired:' onChange={onSecondaryFilterChange} />
      </div>
    )

  const hasAnyFilter = hasFilter || secondaryFilterComponent
  const isInitialLoad = loading && data.length === 0

  if (isInitialLoad) return <Spinner isLoading />

  return (
    <div className={`${styles.root} ${paginationLegend ? styles.rootWithPaginationLegend : ''}`}>
      <div className={styles.controlsWrapper}>
        <div className={styles.searchRow}>
          <Search
            allowClear
            className={styles.search}
            enterButton
            onSearch={handleSearch}
            placeholder="Search..."
          />
          <div className={styles.gearButtonWrap}>
            <Popover
              content={columnPickerContent}
              placement="bottomRight"
              title="Show / Hide Columns"
              trigger="click"
            >
              <SettingOutlined className={styles.columnSettingsIcon} />
            </Popover>
          </div>
        </div>
        {hasAnyFilter && (
          <Collapse
            className={styles.filtersCollapse}
            defaultActiveKey={['filters']}
            expandIconPosition="end"
            ghost
          >
            <Collapse.Panel key="filters" header="Filters">
              {filterJsx}
              {secondaryFilterComponent}
            </Collapse.Panel>
          </Collapse>
        )}
      </div>
      <AntDTable
        bordered={bordered}
        columns={columnsWithFilterSort}
        dataSource={data}
        loading={{ spinning: loading, tip: 'Loading' }}
        onChange={handleTableChange}
        pagination={paginationConfig}
        rowClassName={rowClassName}
        rowKey={rowKey}
        showSorterTooltip={false}
        sticky
      />
      {
        paginationText
      }
    </div>
  )
}

Table.propTypes = {
  bordered: PropTypes.bool,
  columns: PropTypes.arrayOf(PropTypes.shape({
    dataIndex: PropTypes.string,
    filters: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.string,
      value: PropTypes.string
    })),
    onFilter: PropTypes.func,
    render: PropTypes.func,
    sorter: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    title: PropTypes.node,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  })).isRequired,
  defaultVisibleColumns: PropTypes.arrayOf(PropTypes.string),
  fetchData: PropTypes.func.isRequired,
  filterKey: PropTypes.string,
  filterOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string
  })),
  filterValue: PropTypes.string,
  paginationLegend: PropTypes.node,
  params: PropTypes.object,
  results: PropTypes.array,
  rowClassName: PropTypes.func,
  rowKey: PropTypes.oneOfType([PropTypes.func, PropTypes.number, PropTypes.string]),
  secondaryFilter: PropTypes.object,
  showSecondaryFilter: PropTypes.bool,
  totalCount: PropTypes.number
}

Table.defaultProps = {
  bordered: true,
  filterOptions: null,
  filterValue: '',
  params: {},
  rowKey: '_id',
  secondaryFilter: {},
  showSecondaryFilter: false
}

export default Table
